/**
 * SN-8356 — the arithmetic behind the horizontal-scroll affordance, kept apart
 * from the DOM so it can be tested at all. jsdom has no layout and never fires a
 * real scroll, so everything here takes NUMBERS; the geometry that produces them
 * is browser acceptance, not a unit test.
 */

/** The three numbers a scrollable element reports about its horizontal axis. */
export type HorizontalScrollMetrics = {
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}

/**
 * Is there content past the right edge that scrolling would reveal?
 *
 * `epsilon` absorbs sub-pixel rounding: a grid whose columns sum to a fraction
 * over its viewport is not "scrollable" in any way a user can act on, and a
 * fade that never goes away at the far right reads as a rendering artefact.
 * Browsers also report a fractional `scrollLeft` at the end of a scroll on
 * fractional-DPI displays, which is the same problem from the other side.
 */
export const hasScrollableRight = (
  { scrollLeft, scrollWidth, clientWidth }: HorizontalScrollMetrics,
  epsilon = 1
): boolean => scrollWidth - clientWidth - scrollLeft > epsilon

/**
 * Total width of the right-PINNED columns, which in AG Grid 36 are sticky cells
 * inside the same scrolling container rather than a separate viewport. That is
 * the distance the fade has to stop short of, or it would sit on top of the
 * pinned actions instead of at the seam where content slides under them.
 *
 * Defensive about its input on purpose: it is fed by a grid API whose columns
 * can report a width before layout has settled, and one NaN would otherwise
 * propagate into a CSS `right` the browser silently discards.
 */
export const pinnedRightWidth = (widths: readonly number[]): number =>
  widths.reduce<number>(
    (total, width) => total + (Number.isFinite(width) && width > 0 ? width : 0),
    0
  )

/**
 * How far in from the wrapper's right edge the fade has to stop.
 *
 * Two terms, because two different things sit between the scrolling content and
 * that edge:
 *   - the chrome between them (the wrapper's border, and AG Grid's own end
 *     spacer for the vertical scrollbar), which the two rects measure; and
 *   - the sticky pinned-right columns, which in v36 live INSIDE the same
 *     scrolling container and so are not part of that measurement.
 *
 * Clamped at 0: a transiently negative rect difference during layout would
 * otherwise push the fade off the right edge entirely.
 */
export const scrollFadeInset = ({
  wrapperRight,
  viewportRight,
  pinnedWidth,
}: {
  wrapperRight: number
  viewportRight: number
  pinnedWidth: number
}): number => Math.max(0, wrapperRight - viewportRight + pinnedWidth)
